<template>
  <div class="intellectual-property-detail edit">
    <cv-inline-notification
      v-if="isShowErrorBanner"
      class="account-audit-error"
      :title="t('media_object_ip_save_submit_error_message')"
      low-contrast
      kind="error"
      @close="handleCloseValidation"
    />

    <Tabs>
      <template #trigger>
        <tab-controls-item
          v-for="(tab, index) in tabs"
          :key="tab.key"
          :class="tabsCssVars(tab.key)"
          @click="handleTabSelected(index)"
        >
          <h5 class="title">{{ tab.title }}</h5>
        </tab-controls-item>
      </template>
      <template #controls>
        <tab-controls-item
          v-for="(tab, index) in tabs"
          :key="tab.key"
          :class="tabsCssVars(tab.key)"
          @click="handleTabSelected(index)"
        >
          <h5 class="title">{{ tab.title }}</h5>
        </tab-controls-item>
      </template>

      <template #content>
        <tab-content-item
          v-if="isTabSelected('ownershipAndLicense')"
          key="ownershipAndLicense"
          :class="tabsCssVars('ownershipAndLicense')"
        >
          <cv-form>
            <div class="field-group metadata">
              <dl v-if="!hideUuid" class="field-set type-text sls-uuid">
                <dt class="field-label">{{ t("media_object_field_label_sls_uid") }}</dt>
                <dd class="field-value">{{ uuid }}</dd>
              </dl>
              <dl class="field-set type-checkbox display-restriction">
                <dt class="field-label">
                  {{ t("media_object_field_label_display_restriction") }}
                </dt>
                <dd class="field-input">
                  <input-checkbox
                    id="slsUse"
                    value="slsUse"
                    kind="white"
                    v-model:selected-values="slsUseSelectedValues"
                    @change="handleSlsUseChanged"
                  >
                    {{ t("media_object_field_label_display_restriction_publish_sls") }}
                  </input-checkbox>
                </dd>
              </dl>
            </div>

            <div class="field-group ownership">
              <dl
                class="field-set type-dropdown copyright-ownership"
                :class="{ mandatory: isMandatory }"
              >
                <dt class="field-label">
                  {{ t("media_object_field_label_copyright_ownership") }}
                </dt>
                <dd class="field-input">
                  <dropdown-component
                    :value="editFormData.owner"
                    kind="white"
                    :invalid-message="error.owner"
                    @change="handleCopyrightOwnershipChange"
                  >
                    <cv-dropdown-item
                      v-for="item in copyrightOwnershipOptions"
                      :key="item.value"
                      :value="item.value"
                    >
                      {{ item.label }}
                    </cv-dropdown-item>
                  </dropdown-component>
                </dd>
              </dl>
              <dl class="field-set type-text copyright-owner" :class="{ mandatory: isMandatory }">
                <dt class="field-label">{{ t("media_object_field_label_copyright_owner") }}</dt>
                <dd class="field-input">
                  <text-input
                    :value="editFormData.copyRightAuthor"
                    :invalid-message="error.copyRightAuthor"
                    :character-limit="useMediaObject_characterLimit"
                    @input="handleUpdateCopyrightAuthor"
                  />
                </dd>
              </dl>
              <dl
                class="field-set type-text owner-acknowledgement"
                :class="{ mandatory: isMandatory }"
              >
                <dt class="field-label">
                  {{ t("media_object_field_label_acknowledgement_owner") }}
                </dt>
                <dd class="field-input">
                  <text-input
                    :value="editFormData.authorAcknowledgement"
                    :invalid-message="error.authorAcknowledgement"
                    :character-limit="useMediaObject_characterLimit"
                    @input="handleUpdateAuthorAcknowledgement"
                  />
                </dd>
              </dl>
              <dl class="field-set type-text author" :class="{ mandatory: isMandatory }">
                <dt class="field-label">{{ t("media_object_field_label_author_creator") }}</dt>
                <dd class="field-input">
                  <text-input
                    :value="editFormData.author"
                    :invalid-message="error.author"
                    :character-limit="useMediaObject_characterLimit"
                    @input="handleUpdateAuthor"
                  />
                </dd>
              </dl>
              <dl
                class="field-set type-text author-attribution"
                :class="{ mandatory: isMandatory }"
              >
                <dt class="field-label">
                  {{ t("media_object_field_label_attribution_author_creator") }}
                </dt>
                <dd class="field-input">
                  <text-input
                    :value="editFormData.authorAttribution"
                    :invalid-message="error.authorAttribution"
                    :character-limit="useMediaObject_characterLimit"
                    @input="handleUpdateAuthorAttribution"
                  />
                </dd>
              </dl>
              <dl class="field-set type-dropdown resource-type" :class="{ mandatory: isMandatory }">
                <dt class="field-label">{{ t("media_object_field_label_resource_type") }}</dt>
                <dd v-if="readonlyResourceType" class="field-value">
                  <div class="resource-type-description">
                    {{ t("media_object_resource_type_readonly_label") }}
                  </div>
                </dd>
                <dd v-else class="field-input">
                  <dropdown-component
                    :value="editFormData.rmsType"
                    kind="white"
                    :invalid-message="error.rmsType"
                    @change="handleResourceTypeChange"
                  >
                    <cv-dropdown-item
                      v-for="item in resourceTypeOptions"
                      :key="item.value"
                      :value="item.value"
                    >
                      {{ item.label }}
                    </cv-dropdown-item>
                  </dropdown-component>
                </dd>
              </dl>
            </div>

            <div class="field-group license">
              <dl class="field-set type-dropdown license-type" :class="{ mandatory: isMandatory }">
                <dt class="field-label">{{ t("media_object_field_label_licence_type") }}</dt>
                <dd class="field-input">
                  <dropdown-component
                    :value="editFormData.licenseType"
                    :invalid-message="error.licenseType"
                    kind="white"
                    @change="handleLicenceTypeChange"
                  >
                    <cv-dropdown-item
                      v-for="item in licenseTypeOptions"
                      :key="item.value"
                      :value="item.value"
                    >
                      {{ item.label }}
                    </cv-dropdown-item>
                  </dropdown-component>
                </dd>
              </dl>
              <dl
                class="field-set type-text copyright-exception"
                :class="{
                  mandatory: isMandatory && !useMediaObject_isDisabledLicenceRemark,
                }"
              >
                <dt class="field-label">
                  {{ t("media_object_field_label_copyright_exception_details") }}
                </dt>
                <dd class="field-input">
                  <text-input
                    :disabled="useMediaObject_isDisabledLicenceRemark"
                    :value="editFormData.exceptionDetails"
                    :invalid-message="error.exceptionDetails"
                    :character-limit="useMediaObject_characterLimit"
                    @input="handleUpdateCopyrightException"
                  />
                </dd>
              </dl>
              <div class="license-duration">
                <dl
                  class="field-set type-date license-start"
                  :class="{
                    mandatory: isMandatory && !useMediaObject_isDisabledLicenceDate,
                  }"
                >
                  <dt class="field-label">
                    {{ t("media_object_field_label_licence_start_date") }}
                  </dt>
                  <dd class="field-input">
                    <DatePicker
                      :class="{ disabled: useMediaObject_isDisabledLicenceDate }"
                      :disabled="useMediaObject_isDisabledLicenceDate"
                      :value="editFormData.startDate"
                      kind="single"
                      date-label=""
                      placeholder="dd Mmm yyyy"
                      :invalid-message="error.startDate"
                      :cal-options="calendarOption"
                      @change="handleLicenseStartChange"
                    />
                  </dd>
                </dl>
                <dl
                  class="field-set type-date license-end"
                  :class="{
                    mandatory: isMandatory && !useMediaObject_isDisabledLicenceDate,
                  }"
                >
                  <dt class="field-label">{{ t("media_object_field_label_licence_end_date") }}</dt>
                  <dd class="field-input">
                    <DatePicker
                      :class="{ disabled: useMediaObject_isDisabledLicenceDate }"
                      :disabled="useMediaObject_isDisabledLicenceDate"
                      :value="editFormData.endDate"
                      kind="single"
                      date-label=""
                      placeholder="dd Mmm yyyy"
                      :invalid-message="error.endDate"
                      :cal-options="calendarOption"
                      @change="handleLicenseEndChange"
                    />
                  </dd>
                </dl>
              </div>
              <dl
                class="field-set type-dropdown licence-territory"
                :class="{ mandatory: isMandatory }"
              >
                <dt class="field-label">{{ t("media_object_field_label_licence_territory") }}</dt>
                <dd class="field-input">
                  <dropdown-component
                    :value="editFormData.territory"
                    kind="white"
                    :invalid-message="error.territory"
                    @change="handleLicenseTerritoryChange"
                  >
                    <cv-dropdown-item
                      v-for="item in licenseTerritoryOptions"
                      :key="item.value"
                      :value="item.value"
                    >
                      {{ item.label }}
                    </cv-dropdown-item>
                  </dropdown-component>
                </dd>
              </dl>
              <dl class="field-set type-dropdown single-use">
                <dt class="field-label">{{ t("media_object_field_label_single_use") }}</dt>
                <dd class="field-input">
                  <dropdown-component
                    :value="getSingleUseLicense"
                    kind="white"
                    @change="handleSingleUseChange"
                  >
                    <cv-dropdown-item
                      v-for="item in singleUseOptions"
                      :key="item.value"
                      :value="item.value"
                    >
                      {{ item.label }}
                    </cv-dropdown-item>
                  </dropdown-component>
                </dd>
              </dl>
              <dl
                class="field-set type-dropdown reproduction-right"
                :class="{ mandatory: isMandatory && useMediaObject_isNonSLS }"
              >
                <dt class="field-label">{{ t("media_object_field_label_reproduction_right") }}</dt>
                <dd class="field-input">
                  <dropdown-component
                    :disabled="!useMediaObject_isNonSLS"
                    :value="editFormData.reproduction"
                    kind="white"
                    :invalid-message="error.reproduction"
                    @change="handleReproductionRightChange"
                  >
                    <cv-dropdown-item
                      v-for="item in reproductionRightOptions"
                      :key="item.value"
                      :value="item.value"
                    >
                      {{ item.label }}
                    </cv-dropdown-item>
                  </dropdown-component>
                </dd>
              </dl>
            </div>

            <div class="field-group rights">
              <dl
                class="field-set type-dropdown downloadable-rights"
                :class="{ mandatory: isMandatory }"
              >
                <dt class="field-label">
                  {{ t("media_object_field_label_downloadable_rights") }}
                </dt>
                <dd class="field-input">
                  <dropdown-component
                    :value="editFormData.downloadableRight"
                    kind="white"
                    :invalid-message="error.downloadableRight"
                    @change="handleDownloadableRightChange"
                  >
                    <cv-dropdown-item
                      v-for="item in downloadableRightsOptions"
                      :key="item.value"
                      :value="item.value"
                    >
                      {{ item.label }}
                    </cv-dropdown-item>
                  </dropdown-component>
                </dd>
              </dl>
              <dl
                class="field-set type-dropdown printing-rights"
                :class="{ mandatory: isMandatory }"
              >
                <dt class="field-label">{{ t("media_object_field_label_printing_rights") }}</dt>
                <dd class="field-input">
                  <dropdown-component
                    :value="editFormData.printableRight"
                    kind="white"
                    :invalid-message="error.printableRight"
                    @change="handlePrintingRightsChange"
                  >
                    <cv-dropdown-item
                      v-for="item in printingRightsOptions"
                      :key="item.value"
                      :value="item.value"
                    >
                      {{ item.label }}
                    </cv-dropdown-item>
                  </dropdown-component>
                </dd>
              </dl>
              <dl class="field-set type-dropdown modification-rights">
                <dt class="field-label">
                  {{ t("media_object_field_label_modification_rights") }}
                </dt>
                <dd class="field-input">
                  <dropdown-component
                    :value="editFormData.modificationRight"
                    kind="white"
                    @change="handleModificationRightsChange"
                  >
                    <cv-dropdown-item
                      v-for="item in modificationRightsOptions"
                      :key="item.value"
                      :value="item.value"
                    >
                      {{ item.label }}
                    </cv-dropdown-item>
                  </dropdown-component>
                </dd>
              </dl>
              <dl
                class="field-set type-dropdown modification-type"
                :class="{
                  mandatory: isMandatory && !useMediaObject_isMandatoryModificationType,
                }"
              >
                <dt class="field-label">
                  {{ t("media_object_field_label_type_of_modification") }}
                </dt>
                <dd class="field-input">
                  <multi-select
                    :label="t('media_object_field_placeholder_select_modification')"
                    :disabled="useMediaObject_isDisabledModificationType"
                    kind="white"
                    class="type-of-modification-allowed"
                    :options="modificationTypeOptions"
                    :value="selectedModificationTypes"
                    selection-feedback="fixed"
                    @change="handleModificationTypeChange"
                  >
                    <template v-if="error.modificationType !== ''" #invalid-message>
                      {{ error.modificationType }}
                    </template>
                  </multi-select>
                </dd>
              </dl>
              <dl class="field-set type-text modification-detail">
                <dt class="field-label">
                  {{ t("media_object_field_label_modification_detail") }}
                </dt>
                <dd class="field-input">
                  <text-input
                    :value="editFormData.modificationDetails"
                    :invalid-message="error.modificationDetails"
                    :character-limit="useMediaObject_characterLimit"
                    @input="handleUpdateModificationDetail"
                  />
                </dd>
              </dl>
              <dl class="field-set type-text license-terms">
                <dt class="field-label">
                  {{ t("media_object_field_label_other_licence_terms") }}
                </dt>
                <dd class="field-input">
                  <text-input
                    :value="editFormData.otherTerms"
                    :character-limit="useMediaObject_characterLimit"
                    @input="handleUpdateLicenseTerms"
                  />
                </dd>
              </dl>
            </div>

            <div class="field-group licensor">
              <dl class="field-set type-text license-fee">
                <dt class="field-label">
                  {{ t("media_object_field_label_commission_licience_fees") }}
                </dt>
                <dd class="field-input">
                  <text-input
                    :value="editFormData.licenseFee"
                    :character-limit="useMediaObject_characterLimit"
                    @input="handleUpdateLicenseFee"
                  />
                </dd>
              </dl>
              <dl
                class="field-set type-text licensor-name"
                :class="{
                  mandatory: isMandatory && useMediaObject_isCopyRightOwnershipLicenseMaterial,
                }"
              >
                <dt class="field-label">{{ t("media_object_field_label_licensor_name") }}</dt>
                <dd class="field-input">
                  <text-input
                    :value="editFormData.licensorName"
                    :character-limit="useMediaObject_characterLimit"
                    :invalid-message="error.licensorName"
                    @input="handleUpdateLicensorName"
                  />
                </dd>
              </dl>
              <dl
                class="field-set type-text licensor-contact"
                :class="{
                  mandatory: isMandatory && useMediaObject_isCopyRightOwnershipLicenseMaterial,
                }"
              >
                <dt class="field-label">{{ t("media_object_field_label_licensor_contact") }}</dt>
                <dd class="field-input">
                  <text-input
                    :value="editFormData.licensorContact"
                    :character-limit="useMediaObject_characterLimit"
                    :invalid-message="error.licensorContact"
                    @input="handleUpdateLicensorContact"
                  />
                </dd>
              </dl>
              <dl
                class="field-set type-text license-material-source"
                :class="{
                  mandatory: isMandatory && useMediaObject_isCopyRightOwnershipLicenseMaterial,
                }"
              >
                <dt class="field-label">
                  {{ t("media_object_field_label_source_of_licensed_material") }}
                </dt>
                <dd class="field-input">
                  <text-input
                    :value="editFormData.licenseSource"
                    :character-limit="useMediaObject_characterLimit"
                    :invalid-message="error.licenseSource"
                    @input="handleUpdateLicenseMaterialSource"
                  />
                </dd>
              </dl>
              <dl
                class="field-set type-text licensor-special-request"
                :class="{
                  mandatory: isMandatory && useMediaObject_isCopyRightOwnershipLicenseMaterial,
                }"
              >
                <dt class="field-label">
                  {{ t("media_object_field_label_special_requests_from_licensor") }}
                </dt>
                <dd class="field-input">
                  <text-input
                    :value="editFormData.licensorRequest"
                    :character-limit="useMediaObject_characterLimit"
                    :invalid-message="error.licensorRequest"
                    @input="handleUpdateLicensorSpecialRequest"
                  />
                </dd>
              </dl>
              <dl class="field-set type-text other-documentation-record">
                <dt class="field-label">
                  {{ t("media_object_field_label_other_documentation_records") }}
                </dt>
                <dd class="field-input">
                  <text-input
                    :value="editFormData.docRecord"
                    :character-limit="useMediaObject_characterLimit"
                    @input="handleUpdateOtherDocumentationRecord"
                  />
                </dd>
              </dl>
              <dl class="field-set type-text location-in-project-resources">
                <dt class="field-label">
                  {{ t("media_object_field_label_location_in_project_resources") }}
                </dt>
                <dd class="field-input">
                  <text-input
                    :disabled="!useMediaObject_isNonSLS"
                    :value="editFormData.location"
                    :character-limit="useMediaObject_characterLimit"
                    @input="handleUpdateLocationInProjectResources"
                  />
                </dd>
              </dl>
              <dl class="field-set type-text background-ip">
                <dt class="field-label">
                  {{ t("media_object_field_label_background_IP") }}
                </dt>
                <dd class="field-input">
                  <text-input
                    :value="editFormData.backgroundIP"
                    :character-limit="useMediaObject_characterLimit"
                    @input="handleUpdateBackgroundIP"
                  />
                </dd>
              </dl>
            </div>
          </cv-form>
        </tab-content-item>
        <tab-content-item
          v-if="isTabSelected('availability')"
          key="availability"
          :class="tabsCssVars('availability')"
        >
          <cv-form class="field-group">
            <dl class="field-set type-text owner-group">
              <dt class="field-label">{{ t("media_object_field_label_owner_group") }}</dt>
              <dd class="field-value">{{ ownerGroup }}</dd>
            </dl>
            <dl class="field-set type-dropdown end-user-access" :class="{ mandatory: isMandatory }">
              <dt class="field-label">{{ t("media_object_field_label_available_to") }}</dt>
              <dd class="field-input">
                <dropdown-component
                  :value="editFormData.availability"
                  kind="white"
                  :invalid-message="error.availability"
                  @change="handleAvailableToChange"
                >
                  <cv-dropdown-item
                    v-for="item in availabilityOptions"
                    :key="item.value"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </cv-dropdown-item>
                </dropdown-component>
              </dd>
            </dl>
            <dl
              class="field-set type-dropdown level"
              :class="{
                mandatory: isMandatory && !useMediaObject_isDisabledLevelSubject,
              }"
            >
              <dt class="field-label">{{ t("search_filter_label_level") }}</dt>
              <dd class="field-input type-dropdown">
                <multi-select
                  :disabled="useMediaObject_isDisabledLevelSubject"
                  kind="white"
                  class="level-filter"
                  :options="levelListOption"
                  :value="selectedLevelIds"
                  selection-feedback="fixed"
                  @change="handleLevelChange"
                >
                  <template v-if="error.levelAndStreams !== ''" #invalid-message>
                    {{ error.levelAndStreams }}
                  </template>
                </multi-select>
              </dd>
            </dl>
            <dl
              class="field-set type-dropdown subject"
              :class="{
                mandatory: isMandatory && !useMediaObject_isDisabledLevelSubject,
              }"
            >
              <dt class="field-label">{{ t("search_resource_detail_subject_header_label") }}</dt>
              <dd class="field-input type-dropdown">
                <multi-select
                  :disabled="useMediaObject_isDisabledLevelSubject"
                  kind="white"
                  class="subject-filter"
                  :label="selectSubjectPlaceholder"
                  :options="subjectOption"
                  :value="selectedSubjectIds"
                  selection-feedback="fixed"
                  @change="handleSubjectChange"
                >
                  <template v-if="error.subjects !== ''" #invalid-message>
                    {{ error.subjects }}
                  </template>
                </multi-select>
              </dd>
            </dl>
          </cv-form>
        </tab-content-item>
      </template>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  ALL_OPTION,
  AVAILABILITY,
  DOWNLOADABLE_RIGHT,
  LICENSE_TYPE,
  MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION,
  MEDIA_OBJECT_COPYRIGHT_TERRITORY,
  MODIFICATION_RIGHT,
  MODIFICATION_TYPE,
  PRINTABLE_RIGHT,
  REPRODUCTION,
  RMS_TYPE,
  SINGLE_USE,
} from "@plugins/media/media.constant";
import { isEmpty } from "@utils/var.check";
import { MediaObjectFormData } from "@plugins/media/types.ts";
import { LevelAndStream } from "@modules/level-and-stream/types";
import { Subject } from "@modules/student-group/student-group-types";
import { useMediaObject } from "@plugins/media/composables/use-media-object.ts";
import MediaUtils from "@plugins/media/media.utils";

const { ipFormData, formError } = inject("ipContext");

// Props
interface Props {
  uuid?: string;
  editFormData: MediaObjectFormData;
  error: Record<string, string>;
  hasErrorInPage: boolean;
  hasSaveIPErrors?: boolean;
  subjectList: Subject[];
  levelList: LevelAndStream[];
  ownerGroup: string;
  hideUuid?: boolean;
  isMandatory?: boolean;
  readonlyResourceType?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  uuid: "",
  hasSaveIPErrors: false,
  hideUuid: false,
  isMandatory: true,
  readonlyResourceType: false,
});

// Composables
const { t } = useI18n();

const {
  useMediaObject_characterLimit,
  useMediaObject_isCopyRightOwnershipLicenseMaterial,
  useMediaObject_isNonSLS,
  useMediaObject_isDisabledLicenceRemark,
  useMediaObject_isDisabledLevelSubject,
  useMediaObject_isDisabledModificationType,
  useMediaObject_isMandatoryModificationType,
  useMediaObject_isDisabledLicenceDate,
  useMediaObject_validateEmptyField,
  useMediaObject_validateCharacterLimit,
  useMediaObject_validateEndDate,
} = useMediaObject(props.editFormData, props.error);

// Emits
const emit = defineEmits<{
  (e: "ipContentChange", payload): void;
  (e: "ipErrorStatus", payload): void;
}>();

// Data
const selectedTab = ref("ownershipAndLicense");
const levelListOption = ref<unknown[]>([]);
const subjectOption = ref<unknown[]>([]);
const copyrightOwnershipOptions = ref<unknown[]>([]);
const resourceTypeOptions = ref<unknown[]>([]);
const licenseTypeOptions = ref<unknown[]>([]);
const licenseTerritoryOptions = ref<unknown[]>([]);
const singleUseOptions = ref<unknown[]>([]);
const reproductionRightOptions = ref<unknown[]>([]);
const downloadableRightsOptions = ref<unknown[]>([]);
const printingRightsOptions = ref<unknown[]>([]);
const modificationRightsOptions = ref<unknown[]>([]);
const modificationTypeOptions = ref<unknown[]>([]);
const selectedModificationTypes = ref<string[]>([]);
const availabilityOptions = ref<unknown[]>([]);
const slsUseSelectedValues = ref<string[]>([]);
const selectedSubjectIds = ref<string[]>([]);
const selectedLevelIds = ref<string[]>([]);
const calendarOption = ref({
  dateFormat: "d M Y",
  allowInput: true,
  disableMobile: "true",
});
const mapRmsType = ref("");

// Computed
const tabs = computed(() => [
  {
    key: "ownershipAndLicense",
    title: t("media_object_label_ownership_licence"),
  },
  {
    key: "availability",
    title: t("media_object_label_end_user_access"),
  },
]);

const isTabSelected = computed(() => (value: string) => value === selectedTab.value);

const tabsCssVars = computed(() => (tab: string) => ({ active: isTabSelected.value(tab) }));

const getSingleUseLicense = computed(() => {
  if (isEmpty(ipFormData?.singleUseLicense)) return null;
  return ipFormData?.singleUseLicense ? "YES" : "NO";
});

const isShowErrorBanner = computed(() => props.hasErrorInPage || props.hasSaveIPErrors);

// Methods
const handleTabSelected = (index: number) => {
  selectedTab.value = tabs.value[index].key;
};

const resetFields = () => {
  ipFormData.copyRightAuthor = "";
  ipFormData.authorAcknowledgement = "";
  ipFormData.author = "";
  ipFormData.authorAttribution = "Author/Creator: ";
  ipFormData.rmsType = "";
  ipFormData.licenseType = "";
  ipFormData.exceptionDetails = "";
  ipFormData.startDate = "";
  ipFormData.endDate = "";
  ipFormData.territory = "";
  ipFormData.singleUseLicense = "";
  ipFormData.downloadableRight = DOWNLOADABLE_RIGHT.NO.value;
  ipFormData.printableRight = PRINTABLE_RIGHT.NO.value;
  ipFormData.modificationRight = MODIFICATION_RIGHT.NO.value;
  ipFormData.modificationType = [];
  selectedModificationTypes.value = [];
  ipFormData.modificationDetails = "";
  ipFormData.otherTerms = "";
  ipFormData.licenseFee = "";
  ipFormData.licensorName = "";
  ipFormData.licensorContact = "";
  ipFormData.licenseSource = "";
  ipFormData.licensorRequest = "";
  ipFormData.docRecord = "";
  ipFormData.location = "";
  ipFormData.backgroundIP = "";
  ipFormData.availability = "";
  ipFormData.levelAndStreams = [];
  ipFormData.subjects = [];
  if (ipFormData.slsUse) {
    ipFormData.reproduction = "";
  } else {
    ipFormData.reproduction = REPRODUCTION.NO.value;
  }
  resetErrors([
    "copyRightAuthor",
    "authorAcknowledgement",
    "author",
    "authorAttribution",
    "rmsType",
    "licenseType",
    "exceptionDetails",
    "startDate",
    "endDate",
    "territory",
    "singleUseLicense",
    "downloadableRight",
    "printableRight",
    "modificationRight",
    "modificationType",
    "modificationDetails",
    "otherTerms",
    "licenseFee",
    "licensorName",
    "licensorContact",
    "licenseSource",
    "licensorRequest",
    "docRecord",
    "location",
    "backgroundIP",
    "availability",
    "levelAndStreams",
    "subjects",
    "reproduction",
  ]);
  emit("ipContentChange", ipFormData);
};

const setMOEOwnedFields = () => {
  Object.assign(ipFormData, MediaUtils.createMoeOwnedIpFields());
  ipFormData.rmsType = mapRmsType.value;
  ipFormData.singleUseLicense = SINGLE_USE.NO.value;
  if (!ipFormData.slsUse) {
    ipFormData.reproduction = REPRODUCTION.YES.value;
  }
  resetErrors([
    "copyRightAuthor",
    "authorAcknowledgement",
    "author",
    "authorAttribution",
    "licenseType",
    "territory",
    "singleUseLicense",
    "downloadableRight",
    "printableRight",
    "modificationRight",
    "availability",
    "reproduction",
  ]);
};

const setMOEOwnedWithLicencedMaterialFields = () => {
  ipFormData.copyRightAuthor = "MOE";
  ipFormData.authorAcknowledgement = "© MOE";
  ipFormData.authorAttribution = "Author/Creator: " + ipFormData.author;
  ipFormData.rmsType = mapRmsType.value;
  ipFormData.downloadableRight = DOWNLOADABLE_RIGHT.NO.value;
  ipFormData.printableRight = PRINTABLE_RIGHT.NO.value;
  ipFormData.modificationRight = MODIFICATION_RIGHT.NO.value;
  resetErrors([
    "copyRightAuthor",
    "authorAcknowledgement",
    "authorAttribution",
    "downloadableRight",
    "printableRight",
    "modificationRight",
  ]);
};

const setMOECoOwnedAndLicencedMaterialAndOthersFields = () => {
  ipFormData.authorAcknowledgement = ipFormData.copyRightAuthor;
  ipFormData.authorAttribution = "Author/Creator: " + ipFormData.author;
  ipFormData.rmsType = mapRmsType.value;
  ipFormData.downloadableRight = DOWNLOADABLE_RIGHT.NO.value;
  ipFormData.printableRight = PRINTABLE_RIGHT.NO.value;
  ipFormData.modificationRight = MODIFICATION_RIGHT.NO.value;
  resetErrors([
    "authorAcknowledgement",
    "authorAttribution",
    "downloadableRight",
    "printableRight",
    "modificationRight",
  ]);
};

const handleSlsUseChanged = (value: string[]) => {
  ipFormData.slsUse = value.length === 1;
  resetErrors(["reproduction"]);
  if (ipFormData.slsUse) {
    ipFormData.reproduction = "";
    ipFormData.location = "";
  } else {
    if (ipFormData.owner === MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION.MOE_OWNED.value) {
      ipFormData.reproduction = REPRODUCTION.YES.value;
    } else {
      ipFormData.reproduction = REPRODUCTION.NO.value;
    }
  }
  emit("ipContentChange", ipFormData);
};

const handleCopyrightOwnershipChange = (value: string) => {
  resetErrors(["owner"]);
  ipFormData.owner = value;
  resetFields();
  if (value === MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION.MOE_OWNED.value) {
    setMOEOwnedFields();
  } else if (value === MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION.MOE_OWNED_WITH_LICENSED_MATERIAL.value) {
    setMOEOwnedWithLicencedMaterialFields();
  } else {
    setMOECoOwnedAndLicencedMaterialAndOthersFields();
  }
  emit("ipContentChange", ipFormData);
};

const handleUpdateCopyrightAuthor = (value: string) => {
  resetErrors(["copyRightAuthor"]);
  validateCharacterLimitForField(value, "copyRightAuthor");
  ipFormData.copyRightAuthor = value;
  if (
    ipFormData.owner !== MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION.MOE_OWNED.value &&
    ipFormData.owner !== MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION.MOE_OWNED_WITH_LICENSED_MATERIAL.value
  ) {
    ipFormData.authorAcknowledgement = "© " + value;
  }
  emit("ipContentChange", ipFormData);
};

const handleUpdateAuthorAcknowledgement = (value: string) => {
  resetErrors(["authorAcknowledgement"]);
  validateCharacterLimitForField(value, "authorAcknowledgement");
  ipFormData.authorAcknowledgement = value;
  emit("ipContentChange", ipFormData);
};

const handleUpdateAuthor = (value: string) => {
  resetErrors(["author"]);
  validateCharacterLimitForField(value, "author");
  ipFormData.author = value;
  ipFormData.authorAttribution = "Author/Creator: " + value;
  emit("ipContentChange", ipFormData);
};

const handleUpdateAuthorAttribution = (value: string) => {
  resetErrors(["authorAttribution"]);
  validateCharacterLimitForField(value, "authorAttribution");
  ipFormData.authorAttribution = value;
  emit("ipContentChange", ipFormData);
};

const handleResourceTypeChange = (value: string) => {
  resetErrors(["rmsType"]);
  ipFormData.rmsType = value;
  emit("ipContentChange", ipFormData);
};

const handleLicenceTypeChange = (value: string) => {
  ipFormData.licenseType = value;
  ipFormData.exceptionDetails = "";
  resetErrors(["licenseType", "exceptionDetails"]);
  if (value !== LICENSE_TYPE.TERM.value) {
    ipFormData.startDate = "";
    ipFormData.endDate = "";
    resetErrors(["startDate", "endDate"]);
  }
  emit("ipContentChange", ipFormData);
};

const handleUpdateCopyrightException = (value: string) => {
  resetErrors(["exceptionDetails"]);
  validateCharacterLimitForField(value, "exceptionDetails");
  ipFormData.exceptionDetails = value;
  emit("ipContentChange", ipFormData);
};

const handleLicenseStartChange = (dateChange: string) => {
  ipFormData.startDate = dateChange;
  useMediaObject_validateEndDate(ipFormData);
  emit("ipContentChange", ipFormData);
};

const handleLicenseEndChange = (dateChange: string) => {
  ipFormData.endDate = dateChange;
  useMediaObject_validateEndDate(ipFormData);
  emit("ipContentChange", ipFormData);
};

const handleLicenseTerritoryChange = (value: string) => {
  ipFormData.territory = value;
  emit("ipContentChange", ipFormData);
};

const handleSingleUseChange = (value: string | null) => {
  if (isEmpty(value)) {
    ipFormData.singleUseLicense = null;
  } else {
    ipFormData.singleUseLicense = value === "YES";
  }
  emit("ipContentChange", ipFormData);
};

const handleReproductionRightChange = (value: string) => {
  ipFormData.reproduction = value;
  emit("ipContentChange", ipFormData);
};

const handleDownloadableRightChange = (value: string) => {
  ipFormData.downloadableRight = value;
  emit("ipContentChange", ipFormData);
};

const handlePrintingRightsChange = (value: string) => {
  ipFormData.printableRight = value;
  emit("ipContentChange", ipFormData);
};

const handleModificationRightsChange = (value: string) => {
  ipFormData.modificationRight = value;
  if (value === MODIFICATION_RIGHT.NO.value || value === MODIFICATION_RIGHT.NA.value) {
    handleModificationTypeChange([]);
  }
  emit("ipContentChange", ipFormData);
};

const handleModificationTypeChange = (value) => {
  resetErrors(["modificationType"]);
  selectedModificationTypes.value = value;
  ipFormData.modificationType = selectedModificationTypes.value;
  resetErrors(["modificationType"]);
  emit("ipContentChange", ipFormData);
};

const handleUpdateModificationDetail = (value: string) => {
  ipFormData.modificationDetails = value;
  resetErrors(["modificationDetails"]);
  validateCharacterLimitForField(value, "modificationDetails");
  emit("ipContentChange", ipFormData);
};

const handleUpdateLicenseTerms = (value: string) => {
  ipFormData.otherTerms = value;
  emit("ipContentChange", ipFormData);
};

const handleUpdateLicenseFee = (value: string) => {
  ipFormData.licenseFee = value;
  emit("ipContentChange", ipFormData);
};

const handleUpdateLicensorName = (value: string) => {
  ipFormData.licensorName = value;
  resetErrors(["licensorName"]);
  validateCharacterLimitForField(value, "licensorName");
  emit("ipContentChange", ipFormData);
};

const handleUpdateLicensorContact = (value: string) => {
  ipFormData.licensorContact = value;
  resetErrors(["licensorContact"]);
  validateCharacterLimitForField(value, "licensorContact");
  emit("ipContentChange", ipFormData);
};

const handleUpdateLicenseMaterialSource = (value: string) => {
  ipFormData.licenseSource = value;
  resetErrors(["licenseSource"]);
  validateCharacterLimitForField(value, "licenseSource");
  emit("ipContentChange", ipFormData);
};

const handleUpdateLicensorSpecialRequest = (value: string) => {
  ipFormData.licensorRequest = value;
  resetErrors(["licensorRequest"]);
  validateCharacterLimitForField(value, "licensorRequest");
  emit("ipContentChange", ipFormData);
};

const handleUpdateOtherDocumentationRecord = (value: string) => {
  ipFormData.docRecord = value;
  emit("ipContentChange", ipFormData);
};

const handleUpdateLocationInProjectResources = (value: string) => {
  ipFormData.location = value;
  emit("ipContentChange", ipFormData);
};

const handleUpdateBackgroundIP = (value: string) => {
  ipFormData.backgroundIP = value;
  emit("ipContentChange", ipFormData);
};

const handleAvailableToChange = (value: string) => {
  ipFormData.availability = value;
  if (value !== AVAILABILITY.RESTRICTED.value) {
    selectedLevelIds.value = [];
    selectedSubjectIds.value = [];
    ipFormData.levelAndStreams = [];
    ipFormData.subjects = [];
    resetErrors(["levelAndStreams", "subjects"]);
  }
  emit("ipContentChange", ipFormData);
};

const handleLevelChange = (value) => {
  resetErrors(["levelAndStreams"]);
  if (isAllLevelsDeselected(value)) {
    selectedLevelIds.value = [];
  } else if (isSomeLevelsDeselected(value)) {
    selectedLevelIds.value = value.filter((val) => val !== ALL_OPTION.value);
  } else if (isAllSelected(value)) {
    selectedLevelIds.value = levelListOption.value.map((level) => level.value);
  } else {
    selectedLevelIds.value = value;
    if (selectedLevelIds.value.length === levelListOption.value.length - 1) {
      selectedLevelIds.value.push(ALL_OPTION.value);
    }
  }
  ipFormData.levelAndStreams = selectedLevelIds.value;
  emit("ipContentChange", ipFormData);
};

const handleSubjectChange = (value) => {
  resetErrors(["subjects"]);
  if (isAllSubjectsDeselected(value)) {
    selectedSubjectIds.value = [];
  } else if (isSomeSubjectsDeselected(value)) {
    selectedSubjectIds.value = value.filter((val) => val !== ALL_OPTION.value);
  } else if (isAllSelected(value)) {
    selectedSubjectIds.value = subjectOption.value.map((subject) => subject.value);
  } else {
    selectedSubjectIds.value = value;
    if (selectedSubjectIds.value.length === subjectOption.value.length - 1) {
      selectedSubjectIds.value.push(ALL_OPTION.value);
    }
  }
  ipFormData.subjects = selectedSubjectIds.value;
  emit("ipContentChange", ipFormData);
};

const handleCloseValidation = () => {
  emit("ipErrorStatus", false);
};

const resetErrors = (fields: string[]) => {
  fields.forEach((field) => {
    formError[field] = "";
  });
};

const populateDropdownOption = () => {
  const dropdownOptions = [
    { source: MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION, target: copyrightOwnershipOptions.value },
    { source: RMS_TYPE, target: resourceTypeOptions.value },
    { source: LICENSE_TYPE, target: licenseTypeOptions.value },
    { source: MEDIA_OBJECT_COPYRIGHT_TERRITORY, target: licenseTerritoryOptions.value },
    { source: SINGLE_USE, target: singleUseOptions.value },
    { source: REPRODUCTION, target: reproductionRightOptions.value },
    { source: DOWNLOADABLE_RIGHT, target: downloadableRightsOptions.value },
    { source: PRINTABLE_RIGHT, target: printingRightsOptions.value },
    { source: MODIFICATION_RIGHT, target: modificationRightsOptions.value },
    { source: AVAILABILITY, target: availabilityOptions.value },
  ];

  dropdownOptions.forEach(({ source, target }) => {
    Object.values(source).forEach((option) => {
      target.push({
        label: t(option.label),
        value: option.value,
      });
    });
  });
  populateModificationTypes();
  populateLevelAndStream();
  populateSubjects();
};

const populateModificationTypes = () => {
  modificationTypeOptions.value = Object.values(MODIFICATION_TYPE).map((option) => ({
    name: t(option.label),
    label: t(option.label),
    value: option.value,
    disabled: false,
  }));
};

const populateSubjects = () => {
  subjectOption.value.push({
    name: t(ALL_OPTION.label),
    label: t(ALL_OPTION.label),
    value: ALL_OPTION.value,
  });
  props.subjectList?.map((subject) =>
    subjectOption.value.push({
      name: subject.subjectName,
      label: subject.subjectName,
      value: subject.subjectId.toString(),
    }),
  );
  selectedSubjectIds.value = ipFormData?.subjects?.map((item) => item.id.toString()) ?? [];
  ipFormData.subjects = selectedSubjectIds.value;
  if (selectedSubjectIds.value.length === subjectOption.value.length - 1) {
    selectedSubjectIds.value.push(ALL_OPTION.value);
  }
};

const populateLevelAndStream = () => {
  levelListOption.value.push({
    name: t(ALL_OPTION.label),
    label: t(ALL_OPTION.label),
    value: ALL_OPTION.value,
  });
  props.levelList?.map((level) =>
    levelListOption.value.push({
      name: level.displayName,
      label: level.displayName,
      value: level.id.toString(),
    }),
  );
  selectedLevelIds.value = ipFormData?.levelAndStreams?.map((item) => item.id.toString()) ?? [];
  ipFormData.levelAndStreams = selectedLevelIds.value;
  if (selectedLevelIds.value.length === levelListOption.value.length - 1) {
    selectedLevelIds.value.push(ALL_OPTION.value);
  }
};

const validateCharacterLimitForField = (value: string, fieldName: string) => {
  if (typeof value === "string" && value.length > useMediaObject_characterLimit) {
    // If the field exceeds the character limit, set an error message
    formError[fieldName] = t("character_limit_invalid_message", {
      num: useMediaObject_characterLimit,
    });
  } else {
    // If it doesn't exceed, clear any previous error message
    formError[fieldName] = "";
  }
};

const isAllSelected = (value: string[]) => value.includes(ALL_OPTION.value);

const isAllSubjectsDeselected = (value: string[]) =>
  selectedSubjectIds.value.includes(ALL_OPTION.value) &&
  !isAllSelected(value) &&
  value.length === subjectOption.value.length - 1;

const isAllLevelsDeselected = (value: string[]) =>
  selectedLevelIds.value.includes(ALL_OPTION.value) &&
  !isAllSelected(value) &&
  value.length === levelListOption.value.length - 1;

const isSomeSubjectsDeselected = (value: string[]) =>
  selectedSubjectIds.value.includes(ALL_OPTION.value) && value.length < subjectOption.value.length;

/** When previously all was selected but now individual level is deselected */
const isSomeLevelsDeselected = (value: string[]) =>
  selectedLevelIds.value.includes(ALL_OPTION.value) && value.length < levelListOption.value.length;

// Lifecycle
onBeforeMount(() => {
  populateDropdownOption();
  if (ipFormData?.slsUse) {
    slsUseSelectedValues.value.push("slsUse");
  }
  if (ipFormData && ipFormData.modificationType) {
    selectedModificationTypes.value = ipFormData.modificationType;
  }
  if (ipFormData && ipFormData.rmsType) {
    mapRmsType.value = ipFormData.rmsType;
  }
  useMediaObject_validateCharacterLimit(ipFormData);
  useMediaObject_validateEndDate(ipFormData);
  if (props.hasErrorInPage) {
    useMediaObject_validateEmptyField(ipFormData);
  }
});
</script>
